import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  LayoutDashboard, 
  FolderKanban, 
  Mail, 
  FileText,
  Users, 
  LogOut, 
  Menu, 
  X,
  User
} from 'lucide-react';
import { adminAuthService } from '../../services/adminAuth';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const user = adminAuthService.getUser();

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/admin/dashboard',
      icon: LayoutDashboard
    },
    {
      name: 'Projects',
      path: '/admin/projects',
      icon: FolderKanban
    },
    {
      name: 'Messages',
      path: '/admin/contacts',
      icon: Mail
    },
    {
      name: 'Quotes',
      path: '/admin/quotes',
      icon: FileText
    },
    {
      name: 'Users',
      path: '/admin/users',
      icon: Users
    }
  ];

  const handleLogout = async () => {
    await adminAuthService.logout();
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-100">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      {/* Top Navigation Bar */}
      <header className="bg-white shadow-md fixed w-full top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-burale-dark hover:text-burale-teal transition-colors"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <img 
              src="/comp-logo.png" 
              alt="BURALE" 
              className="h-10 w-auto"
            />
            <div className="hidden md:block">
              <h1 className="text-lg font-bold text-burale-dark">Admin Portal</h1>
              <p className="text-xs text-gray-600">BURALE Maintainance & Suppliers</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Link 
              to="/admin/profile"
              className="hidden sm:flex items-center space-x-3 bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <User size={20} className="text-burale-teal" />
              <div>
                <p className="text-sm font-semibold text-burale-dark">{user?.name}</p>
                <p className="text-xs text-gray-600">{user?.email}</p>
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside
          className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-burale-dark transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } mt-16 lg:mt-0`}
        >
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    active
                      ? 'bg-burale-teal text-white shadow-lg'
                      : 'text-gray-300 hover:bg-burale-navy hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
            <div className="text-center text-gray-400 text-xs">
              <p>© 2024 BURALE</p>
              <p className="mt-1">Admin Panel v1.0</p>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default AdminLayout;
